import type { NoteVisibility } from '../../types/notes';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../constants/pagination';

// ===== SORT FIELD TYPE =====

export type NoteSortField =
  | 'createdAt'
  | 'updatedAt'
  | 'title'
  | 'author'
  | 'location'
  | 'visibility';

// ===== SLICE INTERFACE =====

export interface NoteFiltersSlice {
  noteFilters: {
    search: string;
    visibility: NoteVisibility | null;
    colors: string[];
    authors: string[];
    dateFrom: Date | null;
    dateTo: Date | null;
    hasLocation: boolean | null;
    location: string;
    page: number;
    pageSize: number;
    sortBy: NoteSortField;
    sortDescending: boolean;

    // Actions
    setSearch: (search: string) => void;
    setVisibility: (visibility: NoteVisibility | null) => void;
    toggleColor: (color: string) => void;
    clearColors: () => void;
    setAuthors: (authorIds: string[]) => void;
    setDateRange: (from: Date | null, to: Date | null) => void;
    setHasLocation: (hasLocation: boolean | null) => void;
    setLocation: (location: string) => void;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    setSortBy: (sortBy: NoteSortField) => void;
    setSortDescending: (sortDescending: boolean) => void;
    resetFilters: () => void;
  };
}

// ===== SLICE CREATOR =====

export const createNoteFiltersSlice = (
  set: (fn: (state: NoteFiltersSlice) => void) => void,
  _get: () => unknown,
  _store: unknown
) => ({
  noteFilters: {
    search: '',
    visibility: null as NoteVisibility | null,
    colors: [] as string[],
    authors: [] as string[],
    dateFrom: null as Date | null,
    dateTo: null as Date | null,
    hasLocation: null as boolean | null,
    location: '',
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: 'updatedAt' as NoteSortField,
    sortDescending: true,

    setSearch: (search: string) => {
      set((state) => {
        state.noteFilters.search = search;
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    setVisibility: (visibility: NoteVisibility | null) => {
      set((state) => {
        state.noteFilters.visibility = visibility;
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    toggleColor: (color: string) => {
      set((state) => {
        const colorIndex = state.noteFilters.colors.indexOf(color);
        if (colorIndex === -1) {
          state.noteFilters.colors.push(color);
        } else {
          state.noteFilters.colors.splice(colorIndex, 1);
        }
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    clearColors: () => {
      set((state) => {
        state.noteFilters.colors = [];
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    setAuthors: (authorIds: string[]) => {
      set((state) => {
        state.noteFilters.authors = authorIds;
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    setDateRange: (from: Date | null, to: Date | null) => {
      set((state) => {
        state.noteFilters.dateFrom = from;
        state.noteFilters.dateTo = to;
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    setHasLocation: (hasLocation: boolean | null) => {
      set((state) => {
        state.noteFilters.hasLocation = hasLocation;
        if (hasLocation !== true) {
          state.noteFilters.location = '';
        }
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    setLocation: (location: string) => {
      set((state) => {
        state.noteFilters.location = location;
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    setPage: (page: number) => {
      set((state) => {
        state.noteFilters.page = page;
      });
    },

    setPageSize: (pageSize: number) => {
      set((state) => {
        state.noteFilters.pageSize = pageSize;
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    setSortBy: (sortBy: NoteSortField) => {
      set((state) => {
        state.noteFilters.sortBy = sortBy;
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    setSortDescending: (sortDescending: boolean) => {
      set((state) => {
        state.noteFilters.sortDescending = sortDescending;
        state.noteFilters.page = DEFAULT_PAGE;
      });
    },

    resetFilters: () => {
      set((state) => {
        state.noteFilters.search = '';
        state.noteFilters.visibility = null;
        state.noteFilters.colors = [];
        state.noteFilters.authors = [];
        state.noteFilters.dateFrom = null;
        state.noteFilters.dateTo = null;
        state.noteFilters.hasLocation = null;
        state.noteFilters.location = '';
        state.noteFilters.page = DEFAULT_PAGE;
        state.noteFilters.pageSize = DEFAULT_PAGE_SIZE;
        state.noteFilters.sortBy = 'updatedAt';
        state.noteFilters.sortDescending = true;
      });
    },
  },
});
